import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { errorText } from "../api/common";
import { useImportCsv } from "../api/queries";
import type { ImportItem } from "../api/types";
import { CenteredSpinner } from "./CenteredSpinner";
import { CopyableText } from "./CopyableText";

interface Props {
  open: boolean;
  onClose: () => void;
  importItem: ImportItem | null;
}

export const ImportCsvModal = ({ open, onClose, importItem }: Props) => {
  const {
    data: csvContent,
    isLoading,
    error,
  } = useImportCsv(importItem?.id ?? null, open);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      {/* `DialogTitle` is an `h2`, and `CopyableText` renders a block (a `Box`
          around a `p`): a phrasing-only parent — another heading, or the
          `body2` paragraph that used to wrap the id — is invalid HTML around
          it. A `div` title keeps the heading text an `h2` inside it. */}
      <DialogTitle component="div">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" component="h2">
            CSV Preview
          </Typography>
          {importItem && (
            <CopyableText
              text={importItem.id}
              maxLength={7}
              typographyProps={{ color: "textSecondary" }}
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <CenteredSpinner />
        ) : error ? (
          <Alert severity="error">
            Couldn't load this import's file: {errorText(error)}
          </Alert>
        ) : (
          <TextField
            multiline
            fullWidth
            minRows={10}
            maxRows={25}
            value={csvContent ?? ""}
            slotProps={{
              input: {
                readOnly: true,
                sx: {
                  fontFamily: "monospace",
                  fontSize: "0.85rem",
                  whiteSpace: "pre",
                  overflowX: "auto",
                },
              },
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            if (csvContent) {
              navigator.clipboard.writeText(csvContent);
            }
          }}
          disabled={!csvContent || isLoading}
          color="primary"
        >
          Copy All
        </Button>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
